import { useRef, useState } from 'react'
import type { Alert } from '../../hooks/useAlerts'
import { useEligibleAssignees } from '../../hooks/useEligibleAssignees'
import { MemberPicker, NotifySelect } from './AssignControls'
import { validateAssignForm, type NotifyOption } from '../../lib/taskAssign'

export function AlertAssignModal({ alert, title, onClose, onSubmit, onOpenQueue }: {
  alert: Alert
  title: string
  onClose: () => void
  onSubmit: (alert: Alert, opts: { assigneeUserId: string; assigneeName: string; notify: NotifyOption; note: string }) => Promise<void>
  onOpenQueue?: () => void
}) {
  const { assignees, loading: assigneesLoading } = useEligibleAssignees(alert.location_id ?? null)
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [notify, setNotify] = useState<NotifyOption>('none')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pending = useRef(false)

  const submit = async () => {
    if (pending.current) return
    const formError = validateAssignForm({ notify, assigneeUserId: assigneeId })
    if (formError) { setError(formError); return }
    if (!assigneeId) { setError('Choose a teammate to own this follow-up.'); return }
    const assigneeName = assignees.find(a => a.user_id === assigneeId)?.name ?? 'a teammate'
    pending.current = true
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(alert, { assigneeUserId: assigneeId, assigneeName, notify, note })
    } catch (e) {
      pending.current = false
      setError(e instanceof Error ? e.message : "Couldn't assign — retry in a moment.")
      setSubmitting(false)
    }
  }

  const cannotSubmit = submitting || assigneesLoading || !assigneeId

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 11px', borderRadius: 9,
    border: '1px solid var(--border-2)', background: 'var(--surface-2)',
    color: 'var(--text-1)', fontSize: 12.5, fontFamily: 'var(--sans)', outline: 'none',
  }
  const labelStyle: React.CSSProperties = {
    display: 'block', fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 700,
    letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 6,
  }

  return (
    <div
      onClick={() => { if (!submitting) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        background: 'rgba(8,12,16,0.55)', backdropFilter: 'blur(4px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-dialog-title"
        data-testid="alert-assign-dialog"
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460, background: 'var(--surface-solid)',
          border: '1px solid var(--border-2)', borderRadius: 16,
          boxShadow: 'var(--card-shadow-lg)', padding: '22px 22px 18px',
        }}
      >
        <div id="assign-dialog-title" style={{ fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 6 }}>
          Assign alert
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', lineHeight: 1.35, marginBottom: 4, letterSpacing: '-0.01em' }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 18, lineHeight: 1.5 }}>
          Hand this off to a teammate. It's filed on <strong style={{ color: 'var(--text-2)', fontWeight: 600 }}>Today</strong> as their task to follow up on.
        </div>

        <div style={{ marginBottom: 14 }} data-testid="assign-assignee">
          <MemberPicker assignees={assignees} value={assigneeId} onChange={setAssigneeId} loading={assigneesLoading} disabled={submitting} emptyHint="No active teammates have access to this location yet. Add them in Team Management before assigning." />
        </div>

        <div style={{ marginBottom: 14 }}>
          <NotifySelect value={notify} onChange={setNotify} disabled={submitting} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle} htmlFor="assign-note">Note <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>(optional)</span></label>
          <textarea
            id="assign-note"
            data-testid="assign-note"
            disabled={submitting}
            maxLength={4000}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="What should they do about this?"
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' as const, minHeight: 62 }}
          />
        </div>

        {error && (
          <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12, fontWeight: 600 }}>{error}</div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            data-testid="assign-submit"
            onClick={submit}
            disabled={cannotSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: 'var(--cta-fg-on-pos)', fontSize: 12, fontWeight: 700, cursor: cannotSubmit ? 'not-allowed' : 'pointer', opacity: cannotSubmit ? 0.55 : 1, fontFamily: 'var(--sans)', boxShadow: 'var(--cta-shadow)' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            {submitting ? 'Assigning…' : 'Assign alert'}
          </button>
          <button
            onClick={() => { if (!submitting) onClose() }}
            style={{ padding: '9px 14px', borderRadius: 9, border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)' }}
          >
            Cancel
          </button>
          {onOpenQueue && <button
            onClick={onOpenQueue}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)' }}
          >
            Open Today →
          </button>}
        </div>
      </div>
    </div>
  )
}

